import { Alert, AlertTitle, Box, Stack, Typography } from '@mui/material'
import { useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'

import { SummaryValidation } from '@components/prereg_validation/summary_validation'
import { isUserLoggedIn } from '@providers/session/helpers'
import { useSessionContext } from '@providers/session/session_context'
import { MAX_WIDGET_SIZE } from '@shared/constants'
import { i18WithDefault as t } from '@shared/locale'
import { useRoutes } from 'app/use_routes'
import { FlexCardDisplay } from 'components-ui/src/flex_card_display/flex_card_display'
import { InvoiceCard } from 'components-ui/src/purchase_summary/invoice_card/invoice_card'
import { getPoliciesData } from 'components-ui/src/purchase_summary/purchase_receipt/helper'
import { PurchaseReceipt } from 'components-ui/src/purchase_summary/purchase_receipt/purchase_receipt'
import { SurchargePolicy } from 'components-ui/src/purchase_summary/surcharge_policy/surcharge_policy'
import { allowNoPayments } from 'components-ui/src/shared/location'
import { getOrderPrimaryServiceTypeName, isVisitOrder } from 'components-ui/src/shared/orders'
import { Spacer } from 'components-ui/src/spacer/spacer'
import { useClientBookingConfigs } from 'service-api/src/hooks/use_client_booking_configs'
import { useClientOrder } from 'service-api/src/hooks/use_client_order'
import { useClientReceipt } from 'service-api/src/hooks/use_client_receipt'
import { OrderClientDto } from 'service-api/src/types/api'
import { ReceiptDtoCustom } from 'service-api/src/types/general'
import { PurchaseSummarySkeleton } from './purchase_summary_skeleton'

const bookingCardForST = ['boarding', 'daycare', 'grooming', 'temper-test']

export const PurchaseSummary = () => {
  const sessionCtx = useSessionContext()
  const { navigateRoute } = useRoutes()
  const { accountName = '', bookingId = '' } = useParams()
  const [order, setOrder] = useState<OrderClientDto | undefined>()
  const {
    data: orderResponse,
    isFetching: isOrderLoading,
    isSuccess: isOrderSuccess,
    refetch: refetchOrder,
    error: orderError,
  } = useClientOrder(accountName, bookingId)
  const {
    data: receipt,
    isFetching: isReceiptLoading,
    refetch: refetchReceipt,
  } = useClientReceipt(accountName, bookingId)

  const isVisit = isVisitOrder(order)
  const bookingConfs = useClientBookingConfigs(accountName, order?.primaryServiceType?.serviceType?.name || '', {
    enabled: isVisit,
  })
  const hideRefundPolicy = !isVisit || allowNoPayments(order?.primaryServiceType?.locationBookingConf?.[0])
  const policyCardsData = useMemo(
    () => (receipt ? getPoliciesData(receipt, hideRefundPolicy) : []),
    [receipt, hideRefundPolicy]
  )
  const loggedIn = isUserLoggedIn(sessionCtx.state.user)

  useEffect(() => {
    document.title = t('title.purchaseSummary')
  }, [])

  useEffect(() => {
    if (!isOrderSuccess) {
      return
    }

    if (!loggedIn) {
      setTimeout(
        () =>
          navigateRoute(
            'signIn',
            {},
            {
              state: {
                redirect: {
                  route: 'purchaseSummary',
                  params: { bookingId },
                },
                email: orderResponse?.userData?.[0]?.email,
                userExists: !!(orderResponse.ownerId || orderResponse.owner?.id),
              },
            }
          ),
        0
      )
      return
    }

    setOrder(orderResponse)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderResponse, isOrderSuccess, loggedIn, sessionCtx.state.user?.email])

  useEffect(() => {
    if (orderError) {
      setTimeout(() => navigateRoute('notFound', {}), 0)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderError])

  const showBookingCard = useMemo(() => {
    if (isOrderLoading) {
      return false
    }
    return bookingCardForST.includes(order?.primaryServiceType?.serviceType?.name || '')
  }, [isOrderLoading, order?.primaryServiceType?.serviceType?.name])

  if (isOrderLoading || isReceiptLoading || bookingConfs.isFetching) {
    return <PurchaseSummarySkeleton />
  }

  if (!order?.invoices) {
    return null
  }

  const isGrooming = getOrderPrimaryServiceTypeName(order) === 'grooming'

  return (
    <Stack spacing={3} sx={{ maxWidth: MAX_WIDGET_SIZE, p: 3, pb: '130px', margin: '0 auto' }}>
      <Box>
        <Spacer height={3} />
        <Typography variant="h4">{t('purchaseSummary.title')}</Typography>
        <SummaryValidation orderId={order.id || ''} />
      </Box>
      <Stack spacing={3}>
        {order.invoices.map((invoice) => (
          <InvoiceCard
            key={invoice.id}
            invoice={invoice}
            order={order}
            onCancelInvoice={() => {
              refetchOrder()
              refetchReceipt()
            }}
          />
        ))}
      </Stack>
      {/* {isIntakeEnabled(bookingConfs.data?.locationBookingConf) && (
        <Intake
          flexBookingConfs={bookingConfs.data?.flexBookingConfs || []}
          order={order || {}}
          onSave={(newOrder) => {
            setOrder(newOrder)
          }}
        />
      )} */}
      <Stack direction="row" flexWrap="wrap" gap={1}>
        <Stack spacing={3} sx={{ flex: '1 0 49%' }}>
          {showBookingCard && (
            <>
              <Box>
                {isGrooming && (
                  <Alert severity="info">
                    <AlertTitle>{t('paymentForm.groomRequestRefund')}</AlertTitle>
                    {t('paymentForm.groomRequestNotice')}
                  </Alert>
                )}
                {policyCardsData.map((group, index) => (
                  <FlexCardDisplay key={index} cardData={group} />
                ))}
              </Box>
              <Box>
                {bookingConfs.isSuccess && (
                  <SurchargePolicy order={order} bookingConfig={bookingConfs.data} receipt={receipt} />
                )}
              </Box>
            </>
          )}
        </Stack>
        <Box sx={{ flex: '1 0 50%', height: 'fit-content' }}>
          <PurchaseReceipt receipt={receipt as ReceiptDtoCustom} isVisit={isVisit} order={order} />
        </Box>
      </Stack>
    </Stack>
  )
}
